import { poppins300, poppins400, poppins600 } from 'theme/common.style';
import { themePink } from 'theme/color';

/**
 * Admin design tokens.
 *
 * Values are lifted from the live retreX screens so the admin console reads as
 * the same product:
 *   - the `#6C2AD2` → `#77CFFE` sidebar gradient and the `#6F61E1` active
 *     label colour from `component/sider.js`
 *   - the `#112347` heading/body ink and `#666666` muted ink used across
 *     `modules/private` (the two most-used text colours in the app)
 *   - the `#6C2AD2` action purple from the app's links and buttons
 *   - the `#FFFFFF` / `0.25px solid #E4E4E4` / `5px` radius card from
 *     `modules/private/ContactPage`
 *   - the `#fbfbfb` canvas from `modules/AppLayout/AppLayout`
 *   - the `#FAFAFA` header band and `#D9D9D9` rail divider from
 *     `component/AppHeader` and `modules/AppLayout/AppLayout.css`
 *   - the Poppins scale from `theme/common.style`
 */

export const ADMIN_COLORS = {
  /**
   * Links, buttons, focus, tints — the app's dominant purple, used in
   * `component/sider.js`'s gradient and across every user-facing screen.
   */
  accent: '#6C2AD2',
  /** Active navigation label only, exactly as `component/sider.js` sets it. */
  navActive: '#6F61E1',
  accentSoft: '#F1EBFB',
  accentSofter: 'rgba(111, 97, 225, 0.06)',
  gradient: 'linear-gradient(162.58deg, #6c2ad2 1.87%, #77cffe 98.78%)',
  gradientFlat: 'linear-gradient(140.46deg, #6C2AD2 -27.13%, #77CFFE 121.57%)',
  /** The header's active notification block, from `component/AppHeader`. */
  gradientBell: 'linear-gradient(#6C2AD2 0%, #75B8F8 100%)',
  surface: '#FFFFFF',
  canvas: '#fbfbfb',
  /** Header band, as in `component/AppHeader`. */
  headerSurface: '#FAFAFA',
  border: '#E4E4E4',
  borderSoft: '#F0F0F0',
  /** Sider divider from `AppLayout.css`. */
  railBorder: '#D9D9D9',
  text: '#112347',
  textMuted: '#666666',
  textFaint: '#9C9C9C',
  pink: themePink,
  danger: '#F94839',
  warning: '#F79009',
  success: '#10D35E',
};

/**
 * Rail and header follow the app shell exactly: `component/sider.js` is 253px
 * expanded / 106px collapsed, and the `component/AppHeader` band is 85px tall.
 * Admin labels are longer than the app's, so they wrap rather than widening the
 * rail (see `whiteSpace: 'normal'` in `AdminSidebar`).
 */
export const ADMIN_RAIL_WIDTH = 106;
export const ADMIN_RAIL_WIDTH_EXPANDED = 253;
export const ADMIN_HEADER_HEIGHT = 85;

/**
 * `#root` is scaled by `--app-scale` (see index.css), so a plain `100vh`
 * inside it renders short. The rest of the app divides by the same variable —
 * e.g. `.highest-offer-height-spacer` — and so does the admin shell.
 */
export const ADMIN_VIEWPORT_HEIGHT = 'calc(100vh / var(--app-scale, 1))';

/**
 * Portal target for admin drawers. Keeps them inside the scaled `#root` so
 * they render at the same size as everything else (MUI dialogs already do this
 * through the theme in `AppRoutes`, and menus through `index.css`).
 */
export const adminPortalContainer = () => document.getElementById('root');

/** One spacing scale for the whole console, so gutters never disagree. */
export const ADMIN_SPACING = {
  /** MUI <Grid spacing> between cards. */
  grid: 2.5,
  /** Padding inside a card. */
  card: '24px',
  /** Gap between stacked sections on a page. */
  section: '20px',
};

export const adminPageWrap = {
  padding: { xs: '18px 16px 48px', sm: '22px 24px 56px', md: '28px 32px 64px' },
  width: '100%',
};

/** The white panel every admin section sits in. */
export const adminCard = {
  background: ADMIN_COLORS.surface,
  border: `0.25px solid ${ADMIN_COLORS.border}`,
  borderRadius: '5px',
  boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.03)',
};

/**
 * Card that fills its grid cell and keeps its footer pinned to the bottom, so
 * a row of cards stays the same height whatever the content length.
 */
export const adminCardStretch = {
  ...adminCard,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
};

/**
 * Page heading. Same convention as the app's own page titles — Tasks, Insights
 * and Pipeline all use `poppins600` at 32px, stepping down to 24px below `sm`.
 */
export const adminPageTitle = {
  ...poppins600,
  fontSize: { xs: '24px', sm: '32px' },
  color: ADMIN_COLORS.text,
  lineHeight: 1.2,
};

/** "Back to Previous", as on Tasks / Contacts / Insights. */
export const adminBackLink = {
  ...poppins300,
  fontSize: '14px !important',
  color: ADMIN_COLORS.accent,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  width: 'fit-content',
};

export const adminSectionTitle = {
  ...poppins600,
  fontSize: '16px !important',
  color: ADMIN_COLORS.text,
};

export const adminHelperText = {
  ...poppins300,
  fontSize: '14px !important',
  color: ADMIN_COLORS.textMuted,
};

/** Small tinted square that sits before a section title, as on the dashboard. */
export const adminSectionIcon = {
  width: '32px',
  height: '32px',
  borderRadius: '6px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: ADMIN_COLORS.accentSoft,
  color: ADMIN_COLORS.accent,
  flexShrink: 0,
};

/* ------------------------------------------------------------------ table */

export const adminTableHeadCell = {
  ...poppins400,
  fontSize: '14px !important',
  fontWeight: '400 !important',
  color: ADMIN_COLORS.textMuted,
  background: ADMIN_COLORS.surface,
  borderBottom: `1px solid ${ADMIN_COLORS.borderSoft}`,
  padding: '14px 16px',
  whiteSpace: 'nowrap',
};

export const adminTableCell = {
  ...poppins300,
  fontSize: '14px !important',
  color: ADMIN_COLORS.text,
  borderBottom: `1px solid ${ADMIN_COLORS.borderSoft}`,
  padding: '14px 16px',
};

/** Section band above a grouped table, as used on the Tasks screen. */
export const adminGroupHeader = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 16px',
  background: '#FCFCFD',
  borderBottom: `1px solid ${ADMIN_COLORS.borderSoft}`,
};

export const adminCountChip = {
  ...poppins300,
  fontSize: '14px !important',
  color: ADMIN_COLORS.textMuted,
  background: '#F2F4F7',
  borderRadius: '20px',
  padding: '3px 12px',
  whiteSpace: 'nowrap',
};

/** Right-aligned "Showing 12 of 12" line above a table. */
export const adminResultCount = {
  ...poppins300,
  fontSize: '14px !important',
  color: ADMIN_COLORS.textFaint,
  whiteSpace: 'nowrap',
};

/** Divider-topped footer strip inside a card. */
export const adminCardFooter = {
  marginTop: 'auto',
  paddingTop: '14px',
  borderTop: `1px solid ${ADMIN_COLORS.borderSoft}`,
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  flexWrap: 'wrap',
};

/* ---------------------------------------------------------------- filters */

export const adminFilterPill = (isActive) => ({
  ...poppins400,
  fontSize: '14px !important',
  fontWeight: '400 !important',
  textTransform: 'none',
  borderRadius: '20px',
  padding: '5px 18px',
  minWidth: 'auto',
  boxShadow: 'none',
  color: isActive ? ADMIN_COLORS.accent : ADMIN_COLORS.textMuted,
  background: isActive ? ADMIN_COLORS.accentSoft : ADMIN_COLORS.surface,
  border: `1px solid ${isActive ? 'transparent' : ADMIN_COLORS.border}`,
  '&:hover': {
    background: isActive ? ADMIN_COLORS.accentSoft : ADMIN_COLORS.accentSofter,
    border: `1px solid ${isActive ? 'transparent' : ADMIN_COLORS.border}`,
  },
});

/**
 * Pill-shaped search field. Radius, type size and placeholder match the app
 * header's search box in `component/AppHeader`.
 */
export const adminSearchField = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '30px',
    fontFamily: 'Poppins',
    fontSize: '14px',
    background: ADMIN_COLORS.surface,
    '& fieldset': { borderColor: ADMIN_COLORS.border },
    '&:hover fieldset': { borderColor: ADMIN_COLORS.border },
    '&.Mui-focused fieldset': { borderColor: ADMIN_COLORS.accent },
  },
  '& input::placeholder': {
    color: ADMIN_COLORS.textMuted,
    fontSize: '14px',
    fontWeight: 400,
    fontFamily: 'poppins',
    opacity: 1,
  },
};

export const adminSearchButton = {
  ...poppins400,
  fontSize: '14px !important',
  fontWeight: '400 !important',
  textTransform: 'none',
  borderRadius: '30px',
  height: '40px',
  padding: '0 20px',
  color: '#FFFFFF',
  backgroundImage: ADMIN_COLORS.gradientFlat,
  boxShadow: 'none',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  '&:hover': { backgroundImage: ADMIN_COLORS.gradientFlat, opacity: 0.92 },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.7)', opacity: 0.55 },
};


/* ---------------------------------------------------------------- buttons */

/** Outlined header action, e.g. "Last Seen By" / "Invite People". */
export const adminHeaderAction = {
  ...poppins400,
  fontSize: '14px !important',
  fontWeight: '400 !important',
  textTransform: 'none',
  color: ADMIN_COLORS.textMuted,
  background: ADMIN_COLORS.surface,
  border: `1px solid ${ADMIN_COLORS.border}`,
  borderRadius: '8px',
  padding: '7px 16px',
  gap: '8px',
  whiteSpace: 'nowrap',
  '&:hover': {
    background: ADMIN_COLORS.surface,
    borderColor: ADMIN_COLORS.accent,
    color: ADMIN_COLORS.accent,
  },
};

export const adminPrimaryButton = {
  ...poppins400,
  fontSize: '14px !important',
  fontWeight: '400 !important',
  textTransform: 'none',
  borderRadius: '5px',
  padding: '8px 20px',
  color: '#FFFFFF',
  backgroundImage: ADMIN_COLORS.gradientFlat,
  boxShadow: 'none',
  whiteSpace: 'nowrap',
  '&:hover': { backgroundImage: ADMIN_COLORS.gradientFlat, opacity: 0.92 },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.7)', opacity: 0.55 },
};

export const adminSecondaryButton = {
  ...poppins400,
  fontSize: '14px !important',
  fontWeight: '400 !important',
  textTransform: 'none',
  borderRadius: '5px',
  padding: '8px 20px',
  color: ADMIN_COLORS.textMuted,
  borderColor: ADMIN_COLORS.border,
  whiteSpace: 'nowrap',
  '&:hover': {
    borderColor: ADMIN_COLORS.accent,
    color: ADMIN_COLORS.accent,
    background: 'transparent',
  },
};

/** Tinted row action, e.g. the "Notes" button on Contacts. */
export const adminTintButton = {
  ...poppins400,
  fontSize: '14px !important',
  fontWeight: '400 !important',
  textTransform: 'none',
  borderRadius: '5px',
  padding: '5px 16px',
  color: ADMIN_COLORS.accent,
  background: ADMIN_COLORS.accentSoft,
  boxShadow: 'none',
  '&:hover': { background: '#E7DDF9', boxShadow: 'none' },
  '&.Mui-disabled': { color: ADMIN_COLORS.textFaint, background: '#F5F5F7' },
};

/** Small tinted icon button used inside table rows. */
export const adminRowIconButton = {
  width: '30px',
  height: '30px',
  borderRadius: '5px',
  color: ADMIN_COLORS.accent,
  background: ADMIN_COLORS.accentSoft,
  '&:hover': { background: '#E7DDF9' },
  '&.Mui-disabled': { color: ADMIN_COLORS.textFaint, background: '#F5F5F7' },
};

/** Circular outlined overflow button, as on the Contacts rows. */
export const adminRowMenuButton = {
  width: '30px',
  height: '30px',
  border: `1px solid ${ADMIN_COLORS.accent}`,
  color: ADMIN_COLORS.accent,
  '&:hover': { background: ADMIN_COLORS.accentSoft },
  '&.Mui-disabled': {
    borderColor: ADMIN_COLORS.border,
    color: ADMIN_COLORS.textFaint,
  },
};

export const adminInput = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '5px',
    fontFamily: 'Poppins',
    fontSize: '14px',
    '& fieldset': { borderColor: ADMIN_COLORS.border },
    '&:hover fieldset': { borderColor: ADMIN_COLORS.accent },
    '&.Mui-focused fieldset': { borderColor: ADMIN_COLORS.accent },
  },
  '& .MuiInputLabel-root': { fontFamily: 'Poppins', fontSize: '14px' },
  '& .MuiInputLabel-root.Mui-focused': { color: ADMIN_COLORS.accent },
  '& .MuiFormHelperText-root': { fontFamily: 'Poppins', fontSize: '14px' },
};

/* ----------------------------------------------------------------- chips */

/** Soft role chips, matching the Agent / Solicitor chips on Contacts. */
export const ROLE_CHIP_COLORS = {
  Agent: { color: '#4CA4D6', background: '#E8F2FA' },
  'Linked Agent': { color: '#6F61E1', background: '#F1EBFB' },
  Solicitor: { color: '#0F9D58', background: '#E4F6EC' },
  'Primary Vendor': { color: '#B54708', background: '#FDF2E5' },
  'Secondary Vendor': { color: '#B07A20', background: '#FBF3E3' },
};

export const roleChip = (role) => ({
  ...poppins400,
  fontSize: '14px !important',
  fontWeight: '400 !important',
  borderRadius: '20px',
  padding: '4px 20px',
  display: 'inline-block',
  whiteSpace: 'nowrap',
  color: ROLE_CHIP_COLORS[role]?.color || ADMIN_COLORS.textMuted,
  background: ROLE_CHIP_COLORS[role]?.background || '#F2F4F7',
});

/**
 * Status pill colours, shared by users, the audit log, password management and
 * — while it is parked — ticket management.
 */
export const STATUS_COLORS = {
  Active: { color: '#0F9D58', background: '#E4F6EC' },
  Inactive: { color: '#666666', background: '#F2F4F7' },
  Suspended: { color: '#B42318', background: '#FDECEA' },
  Invited: { color: '#8651D9', background: '#F1EBFB' },
  Success: { color: '#0F9D58', background: '#E4F6EC' },
  Failed: { color: '#B42318', background: '#FDECEA' },
  Blocked: { color: '#B54708', background: '#FDF2E5' },
  Sent: { color: '#175CD3', background: '#E8F1FD' },
  Open: { color: '#B54708', background: '#FDF2E5' },
  'In Progress': { color: '#175CD3', background: '#E8F1FD' },
  Resolved: { color: '#0F9D58', background: '#E4F6EC' },
  Closed: { color: '#666666', background: '#F2F4F7' },
};

export const statusPill = (status) => ({
  ...poppins400,
  fontSize: '14px !important',
  fontWeight: '400 !important',
  borderRadius: '20px',
  padding: '4px 14px',
  display: 'inline-block',
  whiteSpace: 'nowrap',
  color: STATUS_COLORS[status]?.color || ADMIN_COLORS.textMuted,
  background: STATUS_COLORS[status]?.background || '#F2F4F7',
});
